import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef, useId } from 'react';
import { cn } from './cn.js';

// ─────────────────────────────────────────────────────────────────────────────
// Fundo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fundo em camadas + grão. É a técnica de maior impacto do guia: a diferença
 * entre um fundo sólido e uma página que parece ter profundidade.
 */
export interface BackdropProps {
  /** Grão em ecrã inteiro — bonito mas pesado ao rolar; desligar no site de vendas. */
  grain?: boolean;
}

export function Backdrop({ grain = true }: BackdropProps): ReactNode {
  return (
    <>
      <div className="mc-backdrop" aria-hidden="true" />
      {grain ? <div className="mc-grain" aria-hidden="true" /> : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Botões
// ─────────────────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'ghost' | 'quiet' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_BASE =
  'mc-btn inline-flex items-center justify-center gap-2 rounded-mc-pill font-semibold ' +
  'transition-[transform,box-shadow,background-color,border-color] duration-200 ease-mc-out ' +
  'disabled:pointer-events-none disabled:opacity-50 select-none whitespace-nowrap';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // Gradiente metálico + halo externo + duas sombras internas que dão espessura.
  primary:
    'bg-gradient-to-b from-[color:var(--mc-btn-2)] to-[color:var(--mc-btn)] text-[color:var(--mc-on-btn)] shadow-mc-btn ' +
    'hover:-translate-y-px hover:from-[color:var(--mc-btn)] hover:to-[color:var(--mc-btn-hover)] hover:shadow-mc-btn-hover active:translate-y-0',
  ghost:
    'border border-mc-line2 bg-mc-surface-ghost text-mc-ink backdrop-blur-md ' +
    'hover:border-mc-accent/60 hover:bg-mc-surface-ghost-strong hover:-translate-y-px',
  quiet:
    'text-mc-ink2 hover:text-mc-ink hover:bg-mc-surface-hover',
  danger:
    'border border-mc-danger/40 bg-mc-danger/10 text-mc-danger ' +
    'hover:bg-mc-danger/20 hover:border-mc-danger/70',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // Alvos de toque com pelo menos 44px de altura.
  sm: 'min-h-[38px] px-4 text-sm',
  md: 'min-h-[46px] px-7 text-[0.95rem]',
  lg: 'min-h-[54px] px-9 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      className,
      children,
      disabled,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        className={cn(
          BUTTON_BASE,
          BUTTON_VARIANTS[variant],
          BUTTON_SIZES[size],
          fullWidth && 'w-full',
          className,
        )}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading ? <Spinner /> : null}
        {children}
      </button>
    );
  },
);

export interface LinkButtonProps
  extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  function LinkButton(
    { variant = 'primary', size = 'md', fullWidth, className, children, ...rest },
    ref,
  ) {
    return (
      <a
        ref={ref}
        className={cn(
          BUTTON_BASE,
          BUTTON_VARIANTS[variant],
          BUTTON_SIZES[size],
          fullWidth && 'w-full',
          className,
        )}
        {...rest}
      >
        {children}
      </a>
    );
  },
);

export function Spinner({ className }: { className?: string }): ReactNode {
  return (
    <span
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
      aria-hidden="true"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Superfícies
// ─────────────────────────────────────────────────────────────────────────────

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card em destaque: borda sólida no acento + halo uniforme. */
  active?: boolean;
  /** Escurece e apaga os cards vizinhos, criando hierarquia sem badge. */
  dimmed?: boolean;
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const CARD_PADDING = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
} as const;

export function Card({
  active = false,
  dimmed = false,
  interactive = false,
  padding = 'md',
  className,
  children,
  ...rest
}: CardProps): ReactNode {
  return (
    <div
      className={cn(
        'rounded-mc-card border bg-mc-surface-card backdrop-blur-sm transition-all duration-200 ease-mc-out',
        CARD_PADDING[padding],
        active
          ? 'border-mc-accent bg-mc-surface-card-active shadow-mc-glow'
          : 'border-mc-line2',
        dimmed && !active && 'opacity-[0.62]',
        interactive &&
          'mc-card-interactive hover:-translate-y-px hover:border-mc-accent/50 hover:shadow-mc-glow-sm',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Banda elevada. Nunca duas secções seguidas com o mesmo tratamento de fundo. */
export function Band({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={cn(
        'border-y border-mc-line bg-mc-surface-band backdrop-blur-[4px]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  width?: 'content' | 'reading' | 'full';
  /** Densidade vertical. O guia pede 96–128px em desktop. */
  spacing?: 'sm' | 'md' | 'lg';
}

export function Section({
  width = 'content',
  spacing = 'md',
  className,
  children,
  ...rest
}: SectionProps): ReactNode {
  return (
    <section
      className={cn(
        'relative px-4 sm:px-6',
        spacing === 'sm' && 'py-11 md:py-14',
        spacing === 'md' && 'py-14 md:py-24',
        spacing === 'lg' && 'py-16 md:py-32',
        className,
      )}
      {...rest}
    >
      <div
        className={cn(
          'mx-auto w-full',
          width === 'content' && 'max-w-mc-content',
          width === 'reading' && 'max-w-mc-reading',
        )}
      >
        {children}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detalhes luminosos
// ─────────────────────────────────────────────────────────────────────────────

export function Eyebrow({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLParagraphElement>): ReactNode {
  return (
    <p className={cn('mc-mono mc-eyebrow', className)} {...rest}>
      {children}
    </p>
  );
}

export function Rule({ className }: { className?: string }): ReactNode {
  return <hr className={cn('mc-rule', className)} />;
}

/** Ponto de estado com halo. */
export function Dot({
  className,
  pulse = false,
}: {
  className?: string;
  pulse?: boolean;
}): ReactNode {
  return (
    <span
      className={cn(
        'mc-dot inline-block size-[7px] rounded-full bg-mc-accent shadow-mc-glow-sm',
        pulse && 'animate-mc-pulse-ring',
        className,
      )}
      aria-hidden="true"
    />
  );
}

/** Ícone com aura radial. */
export function Seal({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      className={cn(
        'grid size-[62px] place-items-center rounded-[14px] border border-mc-accent text-mc-accent shadow-mc-glow',
        'bg-[var(--mc-surface-seal)]',
        className,
      )}
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

export type BadgeTone = 'accent' | 'neutral' | 'danger' | 'warning';

const BADGE_TONES: Record<BadgeTone, string> = {
  accent: 'border-mc-accent/40 bg-mc-accent/10 text-mc-accent',
  neutral: 'border-mc-line2 bg-mc-surface-ghost text-mc-ink2',
  danger: 'border-mc-danger/40 bg-mc-danger/10 text-mc-danger',
  warning: 'border-mc-warning/40 bg-mc-warning/10 text-mc-warning',
};

export function Badge({
  tone = 'neutral',
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone }): ReactNode {
  return (
    <span
      className={cn(
        'mc-mono inline-flex items-center gap-1.5 rounded-mc-pill border px-3 py-1',
        BADGE_TONES[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formulários
// ─────────────────────────────────────────────────────────────────────────────

const FIELD_BASE =
  'w-full rounded-mc-field border border-mc-line bg-mc-surface-field px-4 py-3 text-mc-ink ' +
  'placeholder:text-mc-ink3 transition-[border-color,box-shadow] duration-200 ' +
  'hover:border-mc-line2 focus:outline-none focus:border-mc-accent focus:shadow-mc-ring ' +
  'aria-[invalid=true]:border-mc-danger aria-[invalid=true]:shadow-mc-ring-danger ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

export interface FieldShellProps {
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: (props: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
  }) => ReactNode;
}

export function FieldShell({
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldShellProps): ReactNode {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label ? (
        <label htmlFor={id} className="mc-mono text-mc-ink3">
          {label}
          {/* O asterisco é decorativo: quem usa leitor de ecrã ouve
              "obrigatório" a partir do próprio `required` do input, e sem o
              `aria-hidden` ouviria também "Senha asterisco". */}
          {required ? (
            <span className="ml-1 text-mc-accent" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {children({
        id,
        'aria-invalid': Boolean(error),
        'aria-describedby': describedBy,
      })}
      {error ? (
        <p id={errorId} className="text-sm text-mc-danger" role="alert">
          {error}
        </p>
      ) : null}
      {hint && !error ? (
        <p id={hintId} className="text-xs text-mc-ink3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string | null;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, wrapperClassName, className, required, ...rest },
  ref,
) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      {(a11y) => (
        <input
          ref={ref}
          className={cn(FIELD_BASE, className)}
          required={required}
          {...a11y}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string | null;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, hint, error, wrapperClassName, className, required, ...rest },
    ref,
  ) {
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        required={required}
        className={wrapperClassName}
      >
        {(a11y) => (
          <textarea
            ref={ref}
            className={cn(FIELD_BASE, 'min-h-32 resize-y', className)}
            required={required}
            {...a11y}
            {...rest}
          />
        )}
      </FieldShell>
    );
  },
);

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string | null;
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { label, hint, error, wrapperClassName, className, required, children, ...rest },
    ref,
  ) {
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        required={required}
        className={wrapperClassName}
      >
        {(a11y) => (
          <select
            ref={ref}
            className={cn(FIELD_BASE, 'appearance-none pr-10', className)}
            required={required}
            {...a11y}
            {...rest}
          >
            {children}
          </select>
        )}
      </FieldShell>
    );
  },
);

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, className, ...rest }, ref) {
    const id = useId();
    return (
      <div className="flex items-start gap-3">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className={cn(
            'mt-0.5 size-[18px] shrink-0 cursor-pointer rounded border border-mc-line2 bg-mc-surface-field',
            'accent-mc-accent focus:outline-none focus-visible:shadow-mc-ring',
            className,
          )}
          {...rest}
        />
        <label htmlFor={id} className="cursor-pointer text-sm text-mc-ink2">
          {label}
        </label>
      </div>
    );
  },
);
